/**
 * Tải dữ liệu từ API vào một màn hình.
 *
 * ⚠️ Tệp này là BẢN SAO của `web/admin/src/hooks/useTaiDuLieu.ts`, cố ý.
 * Hai ứng dụng là hai bundle Vite độc lập, mỗi cái phải chép sang máy khác và
 * `npm install && npm run dev` là chạy. Dựng một gói dùng chung sẽ đổi lấy
 * ~100 dòng trùng bằng một lớp workspace mà cả hai bên đều phải cấu hình. Ở
 * quy mô hai ứng dụng, cái giá đó không đáng. Sửa một bên thì nhớ bên kia.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * VÌ SAO CÓ HOOK NÀY
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Trước đây mỗi trang tự viết lại cùng một đoạn: `useEffect` → `setDangTai`
 * → gọi API → `setDuLieu` hoặc `setLoi`. Mỗi bản thiếu một thứ khác nhau:
 *
 *  · **Chạy đua**: đổi tháng nhanh hai lần thì hai lời gọi cùng bay. Lời gọi
 *    CŨ về sau sẽ ghi đè kết quả mới — bảng công hiện số của tháng trước.
 *  · **`setState` sau khi tháo**: chuyển tab giữa lúc đang tải.
 *  · **Không có đường tải lại** nếu lần đầu hỏng vì mất sóng — chuyện xảy ra
 *    thường xuyên ở công trường.
 *
 * ── Vì sao `dang_tai` KHÔNG phải một `useState` ────────────────────────────
 * Nó được SUY RA trong lúc render bằng cách so khoá của tham số hiện tại với
 * khoá của dữ liệu đã tải xong. Nhờ vậy không có `setState` đồng bộ nào trong
 * effect, và không có lượt render thừa mỗi khi đổi tham số.
 */

import { useCallback, useEffect, useState } from 'react'

/** Tham số được phép — phải so sánh được bằng giá trị. */
type ThamSo = string | number | boolean | null | undefined

interface KetQua<T> {
  du_lieu: T | null
  dang_tai: boolean
  loi: unknown
  tai_lai: () => void
  /** Ghi đè dữ liệu tại chỗ, không gọi lại API. */
  dat_du_lieu: (d: T) => void
}

export function useTaiDuLieu<T>(
  goi: () => Promise<T>,
  thamSo: ThamSo[] = [],
  tuyChon: {
    /** Mili-giây giữa hai lần tự làm mới. Bỏ trống = không tự làm mới. */
    lam_moi_moi?: number
    /** `false` thì chưa gọi — dùng khi còn thiếu tham số bắt buộc. */
    bat?: boolean
  } = {},
): KetQua<T> {
  const { lam_moi_moi, bat = true } = tuyChon

  const [lanTai, setLanTai] = useState(0)
  const [kq, setKq] = useState<{ khoa: string; du_lieu: T | null; loi: unknown }>(
    { khoa: '', du_lieu: null, loi: null },
  )

  const khoa = `${lanTai}|${JSON.stringify(thamSo)}`

  useEffect(() => {
    if (!bat) return
    let huy = false

    void (async () => {
      try {
        const d = await goi()
        if (!huy) setKq({ khoa, du_lieu: d, loi: null })
      } catch (e) {
        if (!huy) setKq({ khoa, du_lieu: null, loi: e })
      }
    })()

    return () => { huy = true }
    // `goi` cố tình KHÔNG nằm trong danh sách: nó thường là một arrow function
    // dựng lại mỗi lần render, đưa vào đây là vòng lặp gọi API vô tận. `khoa`
    // đã gói đủ mọi tham số mà lời gọi phụ thuộc.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [khoa, bat])

  const tai_lai = useCallback(() => setLanTai((n) => n + 1), [])

  // Tự làm mới. Chỉ chạy khi tab đang hiện: PWA nằm trong túi quần cả ngày,
  // để nó gọi API ngầm là đốt pin và đốt dữ liệu di động của nhân viên.
  useEffect(() => {
    if (!lam_moi_moi || !bat) return
    const hen = window.setInterval(() => {
      if (document.visibilityState === 'visible') tai_lai()
    }, lam_moi_moi)
    return () => window.clearInterval(hen)
  }, [lam_moi_moi, bat, tai_lai])

  return {
    du_lieu: kq.du_lieu,
    dang_tai: bat && kq.khoa !== khoa,
    loi: kq.loi,
    tai_lai,
    dat_du_lieu: useCallback((d: T) => setKq((t) => ({ ...t, du_lieu: d })), []),
  }
}
